import { useState, useCallback } from "react";
import { useProfileMutation, normalizeUserData } from "@core/features/profile";
import type { RawUserData } from "@core/features/profile";
import { useFetchAffiliationRoles } from "@core/features/affiliations";
import { useFetchOrganizations } from "@core/features/organization";
import { usePlatformStore } from "@platform/store";
import type { User } from "@platform/types/platform.types";
import type { AffiliationEntry } from "@platform/features/affiliations";
import type { OptionType } from "@themes/pollinator/ui/Select";
import { AffiliationsSubStep } from "./AffiliationsSubStep";

export type Props = {
  /** Program ID */
  programId: number;
  /** Handler for next button (called after successful save) */
  onNext: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
};

/**
 * Container for AffiliationsSubStep that handles data fetching and mutations.
 */
export function AffiliationsSubStepContainer({
  programId: _programId,
  onNext,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
}: Props) {
  const user = usePlatformStore((s) => s.user);
  const setUser = usePlatformStore((s) => s.setUser);

  // Fetch organizations
  const { options: orgOptions, isLoading: orgsLoading } =
    useFetchOrganizations();

  // Fetch affiliation roles
  const { roles, isLoading: rolesLoading } = useFetchAffiliationRoles();

  // Track user-created options
  const [createdOrgs, setCreatedOrgs] = useState<OptionType[]>([]);

  // Convert roles to options format
  const roleOptions: OptionType[] = roles.map((role) => ({
    label: role.name,
    value: String(role.id),
  }));

  // Combine loaded and created options
  const allOrgOptions = [...orgOptions, ...createdOrgs];

  // Initialize from user's existing affiliations if any
  const [affiliations, setAffiliations] = useState<AffiliationEntry[]>(
    (user as unknown as { affiliations?: AffiliationEntry[] })?.affiliations ?? []
  );
  const [_isSubmitting, setIsSubmitting] = useState(false);

  const normalizedUser = user
    ? normalizeUserData(user as unknown as RawUserData)
    : null;

  const { mutateAsync } = useProfileMutation({
    userRole: user?.role || "",
    previousValues: normalizedUser,
    onUserUpdate: (userData) => {
      setUser(userData as User);
    },
  });

  const handleContinue = useCallback(async () => {
    if (!normalizedUser) return;

    setIsSubmitting(true);
    try {
      await mutateAsync({
        ...normalizedUser,
        timezone: normalizedUser.timezone ? [normalizedUser.timezone] : [],
        affiliations: affiliations.length > 0 ? affiliations : undefined,
      } as Parameters<typeof mutateAsync>[0] & { affiliations?: AffiliationEntry[] });
      onNext();
    } catch (error) {
      console.error("Failed to save affiliations:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [affiliations, normalizedUser, mutateAsync, onNext]);

  const handleAffiliationsChange = useCallback((value: AffiliationEntry[]) => {
    setAffiliations(value);
  }, []);

  const handleCreateOrganization = useCallback((name: string) => {
    const newOption: OptionType = {
      label: name,
      value: `new-${name.toLowerCase().replace(/\s+/g, "-")}`,
      data: { isNew: true, name },
    };
    setCreatedOrgs((prev) => {
      const exists = prev.some((opt) => opt.value === newOption.value);
      return exists ? prev : [...prev, newOption];
    });
  }, []);

  return (
    <AffiliationsSubStep
      affiliations={affiliations}
      onAffiliationsChange={handleAffiliationsChange}
      organizationOptions={allOrgOptions}
      roleOptions={roleOptions}
      isOptionsLoading={orgsLoading || rolesLoading}
      onCreateOrganization={handleCreateOrganization}
      maxItems={3}
      onContinue={handleContinue}
      onBack={onBack}
      onSkip={onSkip}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
    />
  );
}

export default AffiliationsSubStepContainer;
